import type { DevIngestionStatus, DevPriceEventsStatusDto } from '@/api';
import type { EnvironmentLabel } from '@/utils/get-environment';

/** Returns true when the text is valid JSON. Setting values are stored as JSON. */
export const isValidJson = (text: string): boolean => {
  if (text.trim() === '') return false;
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

/** Formats a price with up to 2 decimals, the way prices show in the rest of the app. */
export const formatPrice = (value: number): string =>
  value.toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

type IngestionStatusDisplay = {
  label: string;
  variant: 'positive' | 'informative' | 'negative';
  description: string;
};

export const getIngestionStatusDisplay = (
  status: DevIngestionStatus,
): IngestionStatusDisplay => {
  switch (status) {
    case 'Running':
      return {
        label: 'Running',
        variant: 'informative',
        description: 'An ingestion is in progress.',
      };
    case 'Failed':
      return {
        label: 'Failed',
        variant: 'negative',
        description:
          'An ingestion that started after the last scheduled run failed.',
      };
    default:
      return {
        label: 'Succeeded',
        variant: 'positive',
        description:
          'No ingestion is running and none failed since the last scheduled run.',
      };
  }
};

type PriceEventsStatusDisplay = {
  label: 'Normal' | 'Stopped';
  variant: 'positive' | 'negative';
  description: string;
};

export const getPriceEventsStatusDisplay = (
  status: DevPriceEventsStatusDto,
): PriceEventsStatusDisplay => {
  if (status.isNormal) {
    return {
      label: 'Normal',
      variant: 'positive',
      description: 'Processing is enabled and the queue is healthy.',
    };
  }

  const reasons: string[] = [];
  if (status.processingEnabled !== true) {
    reasons.push(
      status.processingEnabled === false
        ? 'enable-price-events-processing is false'
        : 'enable-price-events-processing is missing',
    );
  }
  if (status.queueState !== 'Healthy') {
    reasons.push(
      status.queueState
        ? `the queue is ${status.queueState}`
        : 'the queue has no status',
    );
  }
  return {
    label: 'Stopped',
    variant: 'negative',
    description: `${reasons.join(' and ')}.`.replace(/^./, (c) =>
      c.toUpperCase(),
    ),
  };
};

/** True and false setting values show as tags; other values as JSON text. */
export const getBooleanSettingValue = (json: string): boolean | undefined => {
  const trimmed = json.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  return undefined;
};

/**
 * Settings that cannot be changed from the dashboard. A wrong value has an extreme risk for the business,
 * so a change needs a deliberate, reviewed action outside the interface.
 */
export const LOCKED_SETTING_KEYS: ReadonlySet<string> = new Set([
  'enable-old-bw-flow',
]);

export const LOCKED_SETTING_MESSAGE =
  'This change cannot be done from the interface, due to the extreme risk for business';

export const isSettingLocked = (key: string): boolean =>
  LOCKED_SETTING_KEYS.has(key);

export const SETTINGS_RISK_MESSAGE =
  'Changing settings can disrupt customers and cause severe damage to the business. ' +
  'Only change a setting when you fully understand its effect, and check the new value twice before you save.';

type SettingChangeConfirmation = {
  question: string;
  /** Only for production. */
  productionWarning?: string;
};

export const getSettingChangeConfirmation = (
  environment: EnvironmentLabel,
): SettingChangeConfirmation => ({
  question: `You are doing changes to the ${environment} System! Are you sure?`,
  ...(environment === 'Production' && {
    productionWarning:
      'This is the live system used by customers. A wrong value can stop price processing, ' +
      'send wrong prices and cause severe damage to the business.',
  }),
});
